import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { trackNavInteraction } from '../../helpers/tracking';
import { Link } from 'dibs-elements/exports/Link';
import Logo from 'dibs-logo/exports/Logo';
import HamburgerMenu from 'dibs-icons/exports/legacy/HamburgerMenu';
import Close from 'dibs-icons/exports/Close';
import Circle from 'dibs-icons/exports/legacy/Circle';
import { userServerVars } from '../../userServerVars';
import { useNavContext } from '../../NavContext';
import { useNotificationsConfig } from '../../helpers/useNotificationsConfig';
import { isMobile } from 'dibs-media-query';

import styles from './styles/NavLogoHamburger.scss';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { isNavbarExpandable } from '../../helpers/isNavbarExpandable';

import { type NavLogoHamburger_seller$key } from './__generated__/NavLogoHamburger_seller.graphql';
import { type NavLogoHamburger_viewer$key } from './__generated__/NavLogoHamburger_viewer.graphql';
import { type NavLogoHamburger_notifications$key } from './__generated__/NavLogoHamburger_notifications.graphql';

const viewerFragment = graphql`
    fragment NavLogoHamburger_viewer on Viewer {
        ...useNotificationsConfig_viewer
    }
`;

const sellerFragment = graphql`
    fragment NavLogoHamburger_seller on Seller {
        ...useNotificationsConfig_seller
    }
`;

const notificationsFragment = graphql`
    fragment NavLogoHamburger_notifications on DealerNavBarBadges {
        ...useNotificationsConfig_notifications
    }
`;

type Props = {
    viewer?: NavLogoHamburger_viewer$key;
    seller?: NavLogoHamburger_seller$key;
    notifications?: NavLogoHamburger_notifications$key | null;
};

function useBrandLink(): string {
    const { loginInternal, loginDealer, user, masqueradeSeller } = userServerVars;
    if (masqueradeSeller) {
        return '/dealers';
    } else if (loginInternal) {
        return '/internal';
    } else if (loginDealer) {
        return '/dealers';
    } else if (user) {
        return '/internal';
    } else {
        return '/dealers';
    }
}

export const NavLogoHamburger: FC<Props> = ({
    viewer: viewerRef,
    seller: sellerRef,
    notifications: notificationsRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const notifications = useFragment(notificationsFragment, notificationsRef);

    const brandLink = useBrandLink();
    const { isDealer } = userServerVars;
    const { status, setStatus, setExpandedArea } = useNavContext();
    const isMobileView = isMobile();

    // show alert dot on top of hamburger icon if dropdown items have actionable items
    const notificationsConfig = useNotificationsConfig({ viewer, seller, notifications });
    const hasActionableItems = Object.values(notificationsConfig).some(
        notificationType => !!Number(notificationType?.count?.[0])
    );

    const handleHamburgerClick = (): void => {
        trackNavInteraction({
            action: 'hamburger icon',
            label: 'none',
            interactionType: status === 'expanded' ? 'collapse' : 'expand',
        });
        if (isNavbarExpandable()) {
            if (status === 'expanded') {
                setStatus('collapsed');
                setExpandedArea(null);
            } else {
                setStatus('expanded');
            }
        } else if (status === 'overlay' || status === 'expanded') {
            /**
             * If we are below the desktop width, we should only show the overlay and collapsed
             * state. There is a scenario where the seller has resized the screen from full width
             * to tablet size and it's still expanded. This is why we check for 'expanded' here as
             * well, so we go to a collapsed state in this case.
             */
            setStatus('collapsed');
            setExpandedArea(null);
        } else {
            setStatus('overlay');
        }
    };

    return (
        <div
            className={classnames(dibsCss.flex, dibsCss.h60px, dibsCss.pSmall, {
                [dibsCss.wScreen]: isMobileView,
                [dibsCss.pxMedium]: isMobileView,
                [dibsCss.plMedium]:
                    !isMobileView && (status === 'expanded' || status === 'overlay'),
            })}
        >
            <div
                className={classnames(styles.logo, dibsCss.flex1, {
                    [dibsCss.opacity100]: status === 'expanded' || status === 'overlay',
                    [dibsCss.opacity0]: !isMobileView && status === 'collapsed',
                })}
            >
                <Link
                    dataTn="navbarBrandLink"
                    onClick={() =>
                        trackNavInteraction({
                            action: 'logo',
                            label: 'none',
                            interactionType: 'global nav',
                        })
                    }
                    href={brandLink}
                >
                    <Logo
                        className={classnames(styles.logoSvg, {
                            [dibsCss.fillNoir]: isDealer,
                            [dibsCss.fillWhite]: !isDealer,
                        })}
                    />
                </Link>
            </div>
            <div onClick={handleHamburgerClick}>
                {isMobileView && status === 'overlay' ? (
                    <div
                        className={classnames({
                            [dibsCss.fillNoir]: isDealer,
                            [dibsCss.fillWhite]: !isDealer,
                        })}
                    >
                        <Close variation="24" />
                    </div>
                ) : (
                    <div className={dibsCss.relative}>
                        <HamburgerMenu
                            className={classnames(
                                dibsCss.h20px,
                                dibsCss.alignBottom,
                                dibsCss.cursorPointer,
                                {
                                    [dibsCss.fillNoir]: isDealer,
                                    [dibsCss.fillWhite]: !isDealer,
                                }
                            )}
                        />
                        {isMobileView && hasActionableItems && (
                            <Circle
                                className={classnames(
                                    dibsCss.fillAlertprimary,
                                    dibsCss.absolute,
                                    dibsCss.top0,
                                    dibsCss.leftSmaller,
                                    dibsCss.h10px
                                )}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
